import { Suspense, useRef, createContext, useContext, useEffect } from "react";
import axios from "axios";
import i18n from "i18next";
import Cookies from "js-cookie";
import { initReactI18next } from "react-i18next";
import { useTheme } from "@/comn/hooks";
import dayjs from "dayjs";

i18n.use(initReactI18next).init({
    resources: {},
    lng: Cookies.get("lang") || "en",
    interpolation: { escapeValue: false },
});

const Context = createContext<any>(null);

const fetchLabel = async (lang: any, diff?: any) => {
    let date = "";
    try {
        const idb = await getLabel(lang);
        if (idb) {
            const d = new Date(idb.updated);
            d.setHours(d.getHours() - 12);
            date = `${d.getFullYear()}-${
                d.getMonth() + 1
            }-${d.getDate()} ${d.getHours()}:${d.getMinutes()}:${d.getSeconds()}`;
        }
    } catch (error) {}

    let base;
    let diffQuery = "";
    if (diff && date) {
        diffQuery += `?diff=Y&date=${date}`;
    }
    const path = `/api/v1/ptl/comn/comn/front/lang/${lang}`;
    switch (process.env.REACT_APP_SYSTEM_GROUP) {
        case "tancis":
            base = process.env.REACT_APP_API_PTLI || "";
            break;
        case "tanoga":
        case "tanesw":
            base = process.env.REACT_APP_API_PTLE || "";
            break;
    }
    const { data } = await axios.get(base + path + diffQuery);
    return data;
};

const getLabel = (lang: any) => {
    return new Promise<any>((resolve, reject) => {
        const request = indexedDB.open("DEV");
        request.onupgradeneeded = () => {
            const db = request.result;
            db.createObjectStore("META");
        };
        request.onsuccess = async () => {
            const db = request.result;
            const ts = db.transaction("META", "readonly");
            const os = ts.objectStore("META");
            const get = os.get(lang);
            get.onsuccess = () => {
                resolve(get.result);
            };
        };
    });
};

const updateLabel = (lang: any, value: any) => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open("DEV");
        request.onupgradeneeded = () => {
            const db = request.result;
            db.createObjectStore("META");
        };
        request.onsuccess = async () => {
            const db = request.result;
            const ts = db.transaction("META", "readwrite");
            const os = ts.objectStore("META");
            const get = os.get(lang);
            get.onsuccess = () => {
                if (get.result) {
                    const nextValue = { ...get.result.value, ...value };
                    const record = {
                        ...get.result,
                        value: nextValue,
                        count: Object.keys(nextValue).length,
                        updated: new Date(),
                    };
                    const put = os.put(record, lang);
                    put.onsuccess = () => {
                        // @ts-ignore
                        resolve();
                    };
                } else {
                    const now = new Date();
                    const record = {
                        key: lang,
                        value,
                        count: Object.keys(value).length,
                        created: now,
                        updated: now,
                    };
                    const add = os.add(record, lang);
                    add.onsuccess = () => {
                        // @ts-ignore
                        resolve();
                    };
                }
            };
        };
    });
};

const LabelFetcher = ({ children }: any) => {
    const { $Label } = useContext(Context);
    switch ($Label.current.status) {
        case "fulfilled":
            return children;
        default:
            throw $Label.current.promise;
    }
};

const LabelSuspenser = ({ children }: any) => {
    return <Suspense fallback={<div>label</div>}>{children}</Suspense>;
};

const LanguageObserver = () => {
    const { $Label, set } = useContext(Context);

    const {
        theme: { lang },
    } = useTheme();

    useEffect(() => {
        const status = $Label.current.status;
        if (status === "fulfilled") {
            (async () => {
                try {
                    await set();
                } catch (error) {}
            })();
        }
    }, [lang]);

    return null;
};

const LabelContextProvider = ({ children }: any) => {
    const $ = useRef<any>({
        init: false,
        bundles: {},
        status: "pending",
        promise: null,
    });

    const set = async () => {
        const lang = Cookies.get("lang") || "en";
        if ($.current.bundles[lang]) {
            i18n.changeLanguage(lang);
        } else {
            let label;
            let data;

            try {
                data = await getLabel(lang);
            } catch (error) {
                throw error;
            }

            if (data) {
                label = data.value;
            } else {
                let value;
                try {
                    value = await fetchLabel(lang);
                } catch (error) {
                    throw error;
                }

                updateLabel(lang, value);
                label = value;
            }

            i18n.addResourceBundle(lang, "translation", label);
            i18n.changeLanguage(lang);
            $.current.bundles[lang] = true;
        }
    };

    if ($.current.init === false) {
        $.current.init = true;
        $.current.promise = new Promise(async (resolve, reject) => {
            try {
                await set();
            } catch (error) {
                console.log(error);
            } finally {
                //@ts-ignore
                resolve();
                $.current.status = "fulfilled";
            }
        });
    }

    return (
        <Context.Provider value={{ $Label: $, set }}>
            {children}
            <LanguageObserver />
        </Context.Provider>
    );
};

const LabelContext = {
    Provider: LabelContextProvider,
    Suspenser: LabelSuspenser,
    Fetcher: LabelFetcher,
};

export default LabelContext;

export const sync = async () => {
    try {
        const lang = Cookies.get("lang") || "en";
        const value = await fetchLabel(lang, true);
        updateLabel(lang, value);
        i18n.addResourceBundle(lang, "translation", value, false, false);
        i18n.changeLanguage(lang);
    } catch (error) {
        console.log(error);
    }
};
